import { useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import {
  getPatientById,
  updatePatient,
  getAccountByPatientId,
  updateAccount,
  bulkUpdatePatients,
  createAccount,
  removePatientFromAccount,
  addPatientToAccount,
  checkAccountByMedicalAid,
  removePatientsFromAccount,
  addPatientsToAccount,
  generatePatAccNumber,
  getAccountPatients,
  syncPatientPhone,
} from "../../api";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import React from "react";
import Alert from "react-bootstrap/Alert";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useParams } from "react-router-dom";
import SearchMedAidPatient from "./SearchMedAidFamily";
import AccountSearch from "../SearchAccounts/SearchAccounts";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import MedicalAidsReferenceModal from "./MedicalAidsReferenceModal";
import "./PatientAccounts.css";

/**
 * Build just the patient‐update fields + the previous‐aid snapshot
 */
function movePatientBlock(selectedAccount, prevAid) {
  return {
    patientPreviousMedicalAid: prevAid,
    patientMedicalAid: selectedAccount?.accountMedicalAid,
    patientMedicalAidCode: selectedAccount?.accountMedicalAidCode,
    patientMedicalAidOption: selectedAccount?.accountMedicalAidOption,
    patientMedicalAidOptionCode: selectedAccount?.accountMedicalAidOptionCode,
    patientMedicalAidPlan: selectedAccount?.accountMedicalAidPlan,
    patientMedicalAidPlanCode: selectedAccount?.accountMedicalAidPlanCode,
    patientMedicalAidNumber: selectedAccount?.accountMedicalAidNumber,
    patientMedicalAidDependantCode: "99",
    patientMedicalAidJoinDate: new Date().toISOString().slice(0, 10),
    patientMedicalAidLastCheck: new Date(),
    patientMedicalAidStatus: selectedAccount?.dependantStatus || "",
    patientMediKreditPlanNumber03:
      selectedAccount?.accountMediKreditPlanNumber03 || "",
    patientMediKreditPlanNumber04:
      selectedAccount?.accountMediKreditPlanNumber04 || "",
    patientMediKreditPlanNumber06:
      selectedAccount?.accountMediKreditPlanNumber06 || "",
  };
}

function makePatientBlock(dep, pat, patient, prevAid) {
  const m = dep?.medicalAidInfo || {};
  const joined = dep?.dependantDateJoined
    ? new Date(dep?.dependantDateJoined).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);

  // Build full name from dependant data (Full Names = first name only, Last Name is separate)
  const firstName = dep?.dependantFirstName || pat.patientFirstName || "";
  const lastName = dep?.dependantLastName || pat.patientLastName || "";

  // Map FamilyCheck gender ("Male"/"Female") to dropdown values ("1"/"2")
  const genderMap = { Male: "1", Female: "2" };
  const mappedGender = dep?.gender ? (genderMap[dep.gender] || "") : "";

  return {
    // $push this onto each patient
    patientPreviousMedicalAid: prevAid,
    patientIdNumber: dep?.dependantID || pat.patientIdNumber,
    patientFirstName: firstName,
    patientLastName: lastName,
    patientFullNames: firstName || pat.patientFullNames,
    patientInitials: dep?.dependantInitials || pat.patientInitials,
    patientDOB: dep?.dependantDOB || pat.patientDOB,
    patientGender: mappedGender || pat.patientGender,
    patientDataSource: "familyCheck",
    // patient medical-aid fields used in the loop
    patientMedicalAid: m?.medicalAid || dep?.medicalAid,
    patientMedicalAidCode: m?.medicalAidCode || dep?.medicalAidCode,
    patientMedicalAidOption: m?.medicalAidOption || dep?.medicalAidOption,
    patientMedicalAidOptionCode:
      m?.medicalAidOptionCode || dep?.medicalAidOptionCode,
    patientMedicalAidPlan: m?.medicalAidPlan || dep?.medicalAidPlan,
    patientMedicalAidPlanCode: m?.medicalAidPlanCode || dep?.medicalAidPlanCode,
    patientMedicalAidNumber: m?.ch_id || dep?.ch_id,
    patientMedicalAidDependantCode:
      dep?.dependantCode || pat?.patientMedicalAidDependantCode,
    patientMedicalAidJoinDate: joined,
    patientMedicalAidLastCheck: m?.lastCheck ?? new Date(),
    patientMedicalAidStatus: dep?.dependantStatus || "",
    patientMediKreditPlanNumber03:
      m?.mediKreditPlanNumber03 || dep?.mediKreditPlanNumber03,
    patientMediKreditPlanNumber04:
      m?.mediKreditPlanNumber04 || dep?.mediKreditPlanNumber04,
    patientMediKreditPlanNumber06:
      m?.mediKreditPlanNumber06 || dep?.mediKreditPlanNumber06,
    // Spec: IOD strict separation — clear IOD fields when importing normal fund
    patientIODFund: "",
    patientIODFundCode: "",
    patientIODFundCategory: "",
    patientIODPlan: "",
    patientIODPlanCode: "",
    patientIODOption: "",
    patientIODOptionCode: "",
    patientIODEmployer: "",
    patientIODEmployerWCARegNum: "",
    patientIODDateOfInjury: "",
    patientIODClaimRefNumber: "",
    patientIODPatient: false,
  };
}

/**
 * Build just the account‐update fields
 */
function makeAccountBlock(dep, pat, account) {
  const m = dep?.medicalAidInfo || {};

  // ISO date for joinDate
  const joined = dep?.dependantDateJoined
    ? new Date(dep.dependantDateJoined).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);

  // helper to pick new or fallback-to-old
  const pick = (newVal, oldVal) => (pat.patientMainMember ? newVal : oldVal);

  return {
    // ─── ONLY THESE FIVE ARE CONDITIONAL ───
    accountFirstName: pick(
      dep?.dependantFirstName || pat?.patientFirstName,
      account?.accountFirstName
    ),
    accountLastName: pick(
      dep?.dependantLastName || pat?.patientLastName,
      account?.accountLastName
    ),
    accountInitials: pick(
      dep?.dependantInitials || pat?.patientInitials,
      account?.accountInitials
    ),
    accountDOB: pick(dep?.dependantDOB || pat?.patientDOB, account?.accountDOB),
    accountIdNumber: pick(
      dep?.dependantID || pat?.patientIdNumber,
      account?.accountIdNumber
    ),

    // ─── EVERYTHING ELSE ALWAYS UPDATES ───
    accountMedicalAid: m.medicalAid || dep?.medicalAid,
    accountMedicalAidCode: m.medicalAidCode || dep?.medicalAidCode,
    accountMedicalAidOption: m.medicalAidOption || dep?.medicalAidOption,
    accountMedicalAidOptionCode:
      m.medicalAidOptionCode || dep?.medicalAidOptionCode,
    accountMedicalAidPlan: m.medicalAidPlan || dep?.medicalAidPlan,
    accountMedicalAidPlanCode: m.medicalAidPlanCode || dep?.medicalAidPlanCode,
    accountMedicalAidNumber: m.ch_id || dep?.ch_id,

    status: dep?.dependantStatus || "",
    joinDate: joined,

    MediKreditplanNumber03:
      m.mediKreditPlanNumber03 || dep?.mediKreditPlanNumber03,
    MediKreditplanNumber04:
      m.mediKreditPlanNumber04 || dep?.mediKreditPlanNumber04,
    MediKreditplanNumber06:
      m.mediKreditPlanNumber06 || dep?.mediKreditPlanNumber06,
  };
}

function newAccount(dep, pat) {
  const m = dep?.medicalAidInfo || {};
  const joined = dep?.dependantDateJoined
    ? new Date(dep?.dependantDateJoined).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  return {
    // used by the account‐update $set
    accountFirstName: pat?.patientFirstName,
    accountFullNames: pat?.patientFullNames,
    accountLastName: pat?.patientLastName,
    accountInitials: pat?.patientInitials,
    accountDOB: pat?.patientDOB,
    accountIdNumber: pat?.patientIdNumber,
    accountPatientId: pat?._id,
    accountTitle: pat?.patientTitle,
    accountTelNumberCell: pat?.patientCellNumber,
    accountEmail: pat?.patientEmail,
    accountPractice: pat?.patientPractice,
    accountCreatedBy: pat?.patientCreatedBy,
    accountCreatedDate: pat?.patientCreatedDate,
    accountMedicalAid: m.medicalAid || dep?.medicalAid,
    accountMedicalAidCode: m.medicalAidCode || dep?.medicalAidCode,
    accountMedicalAidOption: m.medicalAidOption || dep?.medicalAidOption,
    accountMedicalAidOptionCode:
      m.medicalAidOptionCode || dep?.medicalAidOptionCode,
    accountMedicalAidPlan: m.medicalAidPlan || dep?.medicalAidPlan,
    accountMedicalAidPlanCode: m.medicalAidPlanCode || dep?.medicalAidPlanCode,
    accountMedicalAidNumber: m.ch_id || dep?.ch_id,

    // these match the route’s use of medicalAidData.status / joinDate
    status: dep?.dependantStatus || "",
    joinDate: joined,

    // these must exist so medicalAidData.MediKreditplanNumber03 etc. line up
    MediKreditplanNumber03:
      m.mediKreditPlanNumber03 || dep?.mediKreditPlanNumber03,
    MediKreditplanNumber04:
      m.mediKreditPlanNumber04 || dep?.mediKreditPlanNumber04,
    MediKreditplanNumber06:
      m.mediKreditPlanNumber06 || dep?.mediKreditPlanNumber06,
  };
}

function PatientEditModal({ patientUpdateId, show, onHide, onSave }) {
  const { id } = useParams();
  const { toast, showToast, hideToast } = useToast();
  const [account, setAccount] = useState();
  const [patientStatus, setPatientStatus] = useState("");
  const [mainMemberPerson, setMainMemberPerson] = useState(null); // ALWAYS main member object (from family check)
  const [importedDependant, setImportedDependant] = useState(null); // the dependant you ticked "Import" (can be null)
  const [moveMode, setMoveMode] = useState("NONE"); // "NONE" | "MOVE_EXISTING" | "CREATE_NEW"

  const [userId, setUserId] = useState("");
  const [practice, setAPractice] = useState([]);
  const [dependantList, setDependantList] = useState([]); // State to store dependants list
  const [selectedDependant, setSelectedDependant] = useState(null); // State to store selected dependant
  const [importInfo, setImportInfo] = useState(false); // State to control the import modal
  const [prevAid, setPrevAid] = useState();
  const [notFoundAlert, setNotFoundAlert] = useState(false); // State to control the not found alert
  const [mainMemberWarning, setMainMemberWarning] = useState(false); // State to control the main member warning
  const [removeMed, setRemoveMed] = useState(false); // State to control the remove action
  const [removeMedBulk, setRemoveMedBulk] = useState(false); // State to control the remove action for bulk update
  const [dependantListMainMember, setDependantListMainMember] = useState(); // State to store selected dependant main member
  const [newMainMemberWarning, setNewMainMemberWarning] = useState(false); // State to control the new main member warning
  const [noMainMemberAlert, setNoMainMemberAlert] = useState(false); // State to control the no main member alert
  const [blocked, setBlocked] = useState(false); // State to control the blocked state
  const [existingAccount, setExistingAccount] = useState(false); // State to control the existing account alert
  const [searchNewAccount, setSearchNewAccount] = useState(false); // State to control the search new account alert
  const [selectedAccount, setSelectedAccount] = useState(null); // State to store selected account
  const [notFoundMMAlert, setNotFoundMMAlert] = useState(false); // State to control the not found main member alert
  const [noExistingAccountAlert, setNoExistingAccountAlert] = useState(false); // State to control the no existing account alert
  const [newDependantWarning, setNewDependantWarning] = useState(false); // State to control the new dependant warning
  const [isLoading, setIsLoading] = useState(false); // State to control loading state
  const [updated, setUpdated] = useState(false); // State to control if the patient has been updated
  const [createOrphanAccount, setCreateOrphanAccount] = useState(false); // State to create account for orphaned patient
  const [showRemoveMedChoice, setShowRemoveMedChoice] = useState(false); // Choice dialog for non-main-member med aid removal
  const [highlightManualFields, setHighlightManualFields] = useState(false); // Highlight dependant code + main member after manual apply
  const [isSysAdmin, setIsSysAdmin] = useState(false);
  const [showMedAidRef, setShowMedAidRef] = useState(false);
  // Patient Information State
  const [patient, setPatient] = useState({});
  const [promoteToMainMember, setPromoteToMainMember] = useState(false); // State to control the promote to main member action
  const [idMismatchAlert, setIdMismatchAlert] = useState(false); // State to control the ID mismatch alert
  const [convertToIOD, setConvertToIOD] = useState(false); // State to convert non-IOD patient to IOD
  const [revertFromIOD, setRevertFromIOD] = useState(false); // State to revert IOD patient back to medical aid
  const [modalStep, setModalStep] = useState("edit"); // "edit" | "account" | "confirm"
  const [changesDiff, setChangesDiff] = useState({ patientDiff: [], accountDiff: [], identityMismatch: false });
  const [orphanAccountData, setOrphanAccountData] = useState(null); // Editable account fields for orphan flow
  const originalPatientRef = React.useRef(null); // snapshot of patient at load time
  const originalAccountRef = React.useRef(null); // snapshot of account at load time

  const lastSigRef = React.useRef("");

  const getAccountPatientIds = (acct) => {
    // pick the one that matches your schema
    return (
      acct?.accountPatientId?.map((p) => p?._id || p) ||
      []
    ).filter(Boolean);
  };

  const buildClearMedicalAidPayload = () => ({
    // used by $push
    patientPreviousMedicalAid: prevAid,

    dependantList: dependantList || [],

    // patient medical aid fields
    patientMedicalAid: "",
    patientMedicalAidCode: "",
    patientMedicalAidOption: "",
    patientMedicalAidOptionCode: "",
    patientMedicalAidPlan: "",
    patientMedicalAidPlanCode: "",
    patientMedicalAidNumber: "",
    patientMedicalAidJoinDate: "",
    patientMedicalAidLastCheck: "",
    patientMedicalAidStatus: "",
    patientMediKreditPlanNumber03: "",
    patientMediKreditPlanNumber04: "",
    patientMediKreditPlanNumber06: "",

    // account medical aid fields
    accountMedicalAid: "",
    accountMedicalAidCode: "",
    accountMedicalAidOption: "",
    accountMedicalAidOptionCode: "",
    accountMedicalAidPlan: "",
    accountMedicalAidPlanCode: "",
    accountMedicalAidNumber: "",
    accountMedicalAidStatus: "",
    accountMedicalAidJoinDate: "",

    // NOTE: these names must match your route (MediKreditplanNumber..)
    MediKreditplanNumber03: "",
    MediKreditplanNumber04: "",
    MediKreditplanNumber06: "",
  });

  const applyFamilySelection = useCallback(
  (patientOrNull, mainMemberOrNull) => {
    // ✅ Handle IOD commit signal
    if (patientOrNull?.__iodCommitted && patientOrNull?.iodInfo) {
      console.log("✅ IOD data received:", patientOrNull.iodInfo);
      
      setPatient((prev) => ({
        ...prev,
        // Set IOD fields
        patientIODFund: patientOrNull.iodInfo.iodFund || "",
        patientIODFundCode: patientOrNull.iodInfo.iodFundCode || "",
        patientIODFundCategory: patientOrNull.iodInfo.iodFundCategory || "",
        patientIODPlan: patientOrNull.iodInfo.iodPlan || "",
        patientIODPlanCode: patientOrNull.iodInfo.iodPlanCode || "",
        patientIODOption: patientOrNull.iodInfo.iodOption || "",
        patientIODOptionCode: patientOrNull.iodInfo.iodOptionCode || "",
        patientIODEmployer: patientOrNull.iodInfo.iodEmployer || "",
        patientIODEmployerWCARegNum: patientOrNull.iodInfo.iodEmployerWCARegNum || "",
        patientIODDateOfInjury: patientOrNull.iodInfo.iodDateOfInjury || "",
        patientIODClaimRefNumber: patientOrNull.iodInfo.iodClaimRefNumber || "",
        patientIODPatient: true,
        // Spec: IOD strict separation — clear regular medical aid fields
        patientMedicalAid: "",
        patientMedicalAidCode: "",
        patientMedicalAidOption: "",
        patientMedicalAidOptionCode: "",
        patientMedicalAidPlan: "",
        patientMedicalAidPlanCode: "",
        patientMedicalAidNumber: "",
        patientMedicalAidDependantCode: "",
        patientMedicalAidStatus: "",
        patientMedicalAidJoinDate: "",
        patientMedicalAidLastCheck: "",
        patientMediKreditPlanNumber03: "",
        patientMediKreditPlanNumber04: "",
        patientMediKreditPlanNumber06: "",
      }));
      
      setUpdated(true);
      return; // ✅ Exit early for IOD updates
    }

    // ✅ Handle manual apply when FamilyCheck returned no dependants
    if (patientOrNull?.__manualApply) {
      const m = patientOrNull.medicalAidInfo || {};
      setPatient((prev) => ({
        ...prev,
        patientPreviousMedicalAid: prevAid,
        patientMedicalAid: m.medicalAid || prev.patientMedicalAid || "",
        patientMedicalAidCode: m.medicalAidCode || prev.patientMedicalAidCode || "",
        patientMedicalAidOption: m.medicalAidOption || prev.patientMedicalAidOption || "",
        patientMedicalAidOptionCode: m.medicalAidOptionCode || prev.patientMedicalAidOptionCode || "",
        patientMedicalAidPlan: m.medicalAidPlan || prev.patientMedicalAidPlan || "",
        patientMedicalAidPlanCode: m.medicalAidPlanCode || prev.patientMedicalAidPlanCode || "",
        patientMedicalAidNumber: m.ch_id || prev.patientMedicalAidNumber || "",
        patientMedicalAidLastCheck: m.lastCheck || new Date().toISOString(),
        patientMedicalAidStatus: prev.patientMedicalAidStatus || "",
        patientMediKreditPlanNumber03: m.mediKreditPlanNumber03 || prev.patientMediKreditPlanNumber03 || "",
        patientMediKreditPlanNumber04: m.mediKreditPlanNumber04 || prev.patientMediKreditPlanNumber04 || "",
        patientMediKreditPlanNumber06: m.mediKreditPlanNumber06 || prev.patientMediKreditPlanNumber06 || "",
        patientDataSource: "familyCheck",
      }));

      // Also update account with the searched medical aid info
      if (account) {
        setAccount((prev) => ({
          ...prev,
          accountMedicalAid: m.medicalAid || prev.accountMedicalAid || "",
          accountMedicalAidCode: m.medicalAidCode || prev.accountMedicalAidCode || "",
          accountMedicalAidOption: m.medicalAidOption || prev.accountMedicalAidOption || "",
          accountMedicalAidOptionCode: m.medicalAidOptionCode || prev.accountMedicalAidOptionCode || "",
          accountMedicalAidPlan: m.medicalAidPlan || prev.accountMedicalAidPlan || "",
          accountMedicalAidPlanCode: m.medicalAidPlanCode || prev.accountMedicalAidPlanCode || "",
          accountMedicalAidNumber: m.ch_id || prev.accountMedicalAidNumber || "",
        }));
      }

      setPrevAid((prev) => ({
        ...prev,
        source: "familyCheck",
        txNumber: m.txNumber || null,
        changedAt: new Date().toISOString(),
      }));

      setUpdated(true);
      setHighlightManualFields(true);
      return;
    }

    // 🔒 Prevent runaway updates for normal flow
    const sig = JSON.stringify({
      p: patientOrNull?.dependantCode || null,
      mm: mainMemberOrNull?.dependantCode || null,
      ch: mainMemberOrNull?.medicalAidInfo?.ch_id || null,
      plan: mainMemberOrNull?.medicalAidInfo?.plan || null,
    });
    if (lastSigRef.current === sig) return;
    lastSigRef.current = sig;

    setImportedDependant(patientOrNull || null);
    setMainMemberPerson(mainMemberOrNull || null);

    // Update prevAid metadata for FamilyCheck source + txNumber
    if (patientOrNull || mainMemberOrNull) {
      setPrevAid((prev) => ({
        ...prev,
        source: "familyCheck",
        txNumber: mainMemberOrNull?.medicalAidInfo?.txNumber ||
                  patientOrNull?.medicalAidInfo?.txNumber || null,
        changedAt: new Date().toISOString(),
      }));
    }

    // Determine if the imported dependant IS the main member
    const isImportedMainMember = !!(
      patientOrNull &&
      mainMemberOrNull &&
      String(patientOrNull.dependantCode) === String(mainMemberOrNull.dependantCode)
    );

    // 1) Update PATIENT only when a dependant is selected for import
    if (patientOrNull) {
      setPatient((prev) => ({
        ...prev,
        ...makePatientBlock(patientOrNull, prev, prev, prevAid),
        patientMainMember: isImportedMainMember,
      }));
    }

    // 2) Apply account changes from main member directly to account state
    if (mainMemberOrNull && account) {
      setAccount((prev) => ({
        ...prev,
        ...makeAccountBlock(mainMemberOrNull, patient, prev),
      }));
    }
  },
  [account, patient, prevAid]
);

  useEffect(() => {
    const fetch = async () => {
      const pid = patientUpdateId || id;
      if (!pid) return;

      const p = await getPatientById(pid);
      if (!p) return;

      setPatient(p);
      originalPatientRef.current = { ...p };

      // Decode user for metadata
      const token = sessionStorage.getItem("user");
      const currentUserId = token ? jwtDecode(token)._id : "";
      if (token) {
        const decoded = jwtDecode(token);
        const roles = Array.isArray(decoded.role) ? decoded.role : [decoded.role].filter(Boolean);
        setIsSysAdmin(roles.includes("SystemAdmin"));
      }

      setPrevAid({
        patientMedicalAid: p?.patientMedicalAid,
        patientMedicalAidCode: p?.patientMedicalAidCode,
        patientMedicalAidOption: p?.patientMedicalAidOption,
        patientMedicalAidOptionCode: p?.patientMedicalAidOptionCode,
        patientMedicalAidPlan: p?.patientMedicalAidPlan,
        patientMedicalAidPlanCode: p?.patientMedicalAidPlanCode,
        patientMedicalAidNumber: p?.patientMedicalAidNumber,
        patientMedicalAidDependantCode: p?.patientMedicalAidDependantCode,
        patientMedicalAidStatus: p?.patientMedicalAidStatus,
        patientMedicalAidLastCheck: p?.patientMedicalAidLastCheck,
        patientMedicalAidJoinDate: p?.patientMedicalAidJoinDate,
        patientMediKreditPlanNumber03: p?.patientMediKreditPlanNumber03,
        patientMediKreditPlanNumber04: p?.patientMediKreditPlanNumber04,
        patientMediKreditPlanNumber06: p?.patientMediKreditPlanNumber06,
        patientMainMember: p?.patientMainMember,
        // Spec: metadata for previousMedicalAid history
        changedAt: new Date().toISOString(),
        changedByUserId: currentUserId,
        source: "manual",
        txNumber: null,
      });
      console.log("Patient fetched:", p);
      if (p?.patientMedicalAid) {
        // (you had setMedicalAidMember(true) but never used it in your UI)
      }
      const acct = await getAccountByPatientId(p._id);
      console.log("Account fetched:", acct);
      if (acct) {
        setAccount(acct);
        originalAccountRef.current = { ...acct };
      } else {
        showToast("error", `This patient (${p.patientFirstName} ${p.patientLastName}) is orphaned — no account found. Click "Create Account" to create one.`);
      }
    };
    fetch();
  }, [id]);

  const handleAccountSearchSelect = (acct) => {
    setSelectedAccount(acct || null);
    setMoveMode(acct ? "MOVE_EXISTING" : "NONE");
  };

  // Handling the change of the input fields
  async function handleChangePatient(e) {
    const { name, checked } = e.target;
    const token = sessionStorage.getItem("user");
    const decoded = jwtDecode(token);
    setUserId(decoded._id);
    const userPractice = decoded.practice;
    setAPractice(userPractice);
    setPatient((prevPatient) => ({
      ...prevPatient,
      [e.target.name]: e.target.value,
      patientCreatedBy: userId,
      patientStatus: patientStatus,
    }));
    setUpdated(true);
  }

  const handleRemove = () => {
    if (patient.patientMainMember) {
      // Main member: bulk-clear entire account, warn user
      setPatient((prev) => ({
        ...prev,
        patientMedicalAid: "",
        patientMedicalAidCode: "",
        patientMedicalAidOption: "",
        patientMedicalAidOptionCode: "",
        patientMedicalAidPlan: "",
        patientMedicalAidPlanCode: "",
        patientMedicalAidNumber: "",
        patientMedicalAidDependantCode: "",
        patientMedicalAidLastCheck: "",
        patientMedicalAidStatus: "",
        patientMedicalAidJoinDate: "",
        patientMediKreditPlanNumber03: "",
        patientMediKreditPlanNumber04: "",
        patientMediKreditPlanNumber06: "",
        patientPreviousMedicalAid: prevAid,
      }));
      setAccount((prev) => ({
        ...prev,
        accountMedicalAid: "",
        accountMedicalAidCode: "",
        accountMedicalAidOption: "",
        accountMedicalAidOptionCode: "",
        accountMedicalAidPlan: "",
        accountMedicalAidPlanCode: "",
        accountMedicalAidNumber: "",
        accountMedicalAidStatus: "",
        accountMedicalAidJoinDate: "",
        accountMediKreditplanNumber03: "",
        accountMediKreditplanNumber04: "",
        accountMediKreditplanNumber06: "",
      }));
      setMainMemberWarning(true);
      setRemoveMedBulk(true);
    } else {
      // Non-main-member: present choice to user
      setShowRemoveMedChoice(true);
    }
  };

  // Choice A: remove med aid from everyone on the account (bulk)
  const handleRemoveMedAll = () => {
    setShowRemoveMedChoice(false);
    setPatient((prev) => ({
      ...prev,
      patientMedicalAid: "",
      patientMedicalAidCode: "",
      patientMedicalAidOption: "",
      patientMedicalAidOptionCode: "",
      patientMedicalAidPlan: "",
      patientMedicalAidPlanCode: "",
      patientMedicalAidNumber: "",
      patientMedicalAidDependantCode: "",
      patientMedicalAidLastCheck: "",
      patientMedicalAidStatus: "",
      patientMedicalAidJoinDate: "",
      patientMediKreditPlanNumber03: "",
      patientMediKreditPlanNumber04: "",
      patientMediKreditPlanNumber06: "",
      patientPreviousMedicalAid: prevAid,
    }));
    setAccount((prev) => ({
      ...prev,
      accountMedicalAid: "",
      accountMedicalAidCode: "",
      accountMedicalAidOption: "",
      accountMedicalAidOptionCode: "",
      accountMedicalAidPlan: "",
      accountMedicalAidPlanCode: "",
      accountMedicalAidNumber: "",
      accountMedicalAidStatus: "",
      accountMedicalAidJoinDate: "",
      accountMediKreditplanNumber03: "",
      accountMediKreditplanNumber04: "",
      accountMediKreditplanNumber06: "",
    }));
    setRemoveMedBulk(true);
  };

  // Choice B: move patient to a new private account with no medical aid
  const handleRemoveMedPrivate = () => {
    setShowRemoveMedChoice(false);
    setMoveMode("PRIVATE_ACCOUNT");
    setPatient((prev) => ({
      ...prev,
      patientMainMember: true,
      patientPreviousMedicalAid: prevAid,
      patientMedicalAid: "",
      patientMedicalAidCode: "",
      patientMedicalAidOption: "",
      patientMedicalAidOptionCode: "",
      patientMedicalAidPlan: "",
      patientMedicalAidPlanCode: "",
      patientMedicalAidNumber: "",
      patientMedicalAidDependantCode: "",
      patientMedicalAidStatus: "",
      patientMedicalAidJoinDate: "",
      patientMedicalAidLastCheck: "",
      patientMediKreditPlanNumber03: "",
      patientMediKreditPlanNumber04: "",
      patientMediKreditPlanNumber06: "",
    }));
    setUpdated(true);
  };

  // helper: clear medical aid fields on the ACCOUNT
  const clearAccountMedicalAid = {
    accountMedicalAid: "",
    accountMedicalAidCode: "",
    accountMedicalAidOption: "",
    accountMedicalAidOptionCode: "",
    accountMedicalAidPlan: "",
    accountMedicalAidPlanCode: "",
    accountMedicalAidNumber: "",
    status: "", // if you store scheme status here
    joinDate: "", // if you store join date here

    MediKreditplanNumber03: "",
    MediKreditplanNumber04: "",
    MediKreditplanNumber06: "",
  };

  const handleUpdateDependantList = (list) => {
    setDependantList(list);
    setDependantListMainMember(list.filter((item) => item?.mainMember));
  };

  const onClickPromoteMainMember = () => {
    setMoveMode("PROMOTE");
    setPromoteToMainMember(true);
    // Clear medical aid + IOD from patient and mark as main member
    setPatient((prev) => ({
      ...prev,
      patientMainMember: true,
      patientPreviousMedicalAid: prevAid,
      // Clear medical aid
      patientMedicalAid: "",
      patientMedicalAidCode: "",
      patientMedicalAidOption: "",
      patientMedicalAidOptionCode: "",
      patientMedicalAidPlan: "",
      patientMedicalAidPlanCode: "",
      patientMedicalAidNumber: "",
      patientMedicalAidDependantCode: "",
      patientMedicalAidStatus: "",
      patientMedicalAidJoinDate: "",
      patientMedicalAidLastCheck: "",
      patientMediKreditPlanNumber03: "",
      patientMediKreditPlanNumber04: "",
      patientMediKreditPlanNumber06: "",
      // Clear IOD
      patientIODFund: "",
      patientIODFundCode: "",
      patientIODFundCategory: "",
      patientIODPlan: "",
      patientIODPlanCode: "",
      patientIODOption: "",
      patientIODOptionCode: "",
      patientIODEmployer: "",
      patientIODEmployerWCARegNum: "",
      patientIODDateOfInjury: "",
      patientIODClaimRefNumber: "",
      patientIODPatient: false,
    }));
    setUpdated(true);
  };

// Spec: OLD vs NEW confirmation — compute diff of changes
function computeFieldDiff(original, current, fields) {
  const diff = [];
  for (const { key, label, format } of fields) {
    const oldVal = original?.[key] ?? "";
    const newVal = current?.[key] ?? "";
    if (String(oldVal) !== String(newVal)) {
      const fmt = format || ((v) => {
        if (v === true) return "Yes";
        if (v === false) return "No";
        return v || "(empty)";
      });
      diff.push({ label, oldVal: fmt(oldVal), newVal: fmt(newVal) });
    }
  }
  return diff;
}

const patientDiffFields = [
  { key: "patientMainMember", label: "Main Member" },
  { key: "patientNumber", label: "Patient Number" },
  { key: "patientTitle", label: "Title" },
  { key: "patientFullNames", label: "Full Name(s)" },
  { key: "patientFirstName", label: "First Name" },
  { key: "patientLastName", label: "Last Name" },
  { key: "patientInitials", label: "Initials" },
  { key: "patientDOB", label: "Date of Birth" },
  { key: "patientIdNumber", label: "ID Number" },
  { key: "patientCellNumber", label: "Mobile Number" },
  { key: "patientEmail", label: "Email" },
  {
    key: "patientGender",
    label: "Gender",
    format: (v) => (v === "1" ? "Male" : v === "2" ? "Female" : v || "(empty)"),
  },
  { key: "patientMedicalAid", label: "Medical Aid" },
  { key: "patientMedicalAidPlan", label: "Plan" },
  { key: "patientMedicalAidOption", label: "Option" },
  { key: "patientMedicalAidNumber", label: "Med Aid Number" },
  { key: "patientMedicalAidDependantCode", label: "Dependant Code" },
  { key: "patientMedicalAidStatus", label: "Med Aid Status" },
  { key: "patientMedicalAidJoinDate", label: "Med Aid Join Date" },
  { key: "patientMediKreditPlanNumber03", label: "MediKredit Plan No. 03" },
  { key: "patientMediKreditPlanNumber04", label: "MediKredit Plan No. 04" },
  { key: "patientMediKreditPlanNumber06", label: "MediKredit Plan No. 06" },
  { key: "patientIODFund", label: "IOD Fund" },
  { key: "patientIODPlan", label: "IOD Plan" },
  { key: "patientIODOption", label: "IOD Option" },
  { key: "patientIODEmployer", label: "IOD Employer" },
  { key: "patientIODEmployerWCARegNum", label: "IOD Employer WCA Reg" },
  { key: "patientIODClaimRefNumber", label: "IOD Claim Ref" },
  { key: "patientIODDateOfInjury", label: "IOD Date of Injury" },
];

const accountDiffFields = [
  { key: "accountFirstName", label: "Account First Name" },
  { key: "accountLastName", label: "Account Last Name" },
  { key: "accountInitials", label: "Account Initials" },
  { key: "accountIdNumber", label: "Account ID Number" },
  { key: "accountMedicalAid", label: "Account Medical Aid" },
  { key: "accountMedicalAidPlan", label: "Account Plan" },
  { key: "accountMedicalAidOption", label: "Account Option" },
  { key: "accountMedicalAidNumber", label: "Account Med Aid Number" },
  { key: "accountMedicalAidStatus", label: "Account Med Aid Status" },
  { key: "accountMedicalAidJoinDate", label: "Account Med Aid Join Date" },
];

function buildOrphanAccountData() {
  return {
    accountNumber: "", // will be auto-generated on save
    accountTitle: patient.patientTitle || "",
    accountFullNames: patient.patientFullNames || "",
    accountFirstName: patient.patientFirstName || "",
    accountLastName: patient.patientLastName || "",
    accountInitials: patient.patientInitials || "",
    accountIdNumber: patient.patientIdNumber || "",
    accountDOB: patient.patientDOB || "",
    accountEmail: patient.patientEmail || "",
    accountTelNumberCell: patient.patientCellNumber || "",
    accountPractice: patient.patientPractice || undefined,
    accountMedicalAid: patient.patientMedicalAid || "",
    accountMedicalAidCode: patient.patientMedicalAidCode || "",
    accountMedicalAidOption: patient.patientMedicalAidOption || "",
    accountMedicalAidOptionCode: patient.patientMedicalAidOptionCode || "",
    accountMedicalAidPlan: patient.patientMedicalAidPlan || "",
    accountMedicalAidPlanCode: patient.patientMedicalAidPlanCode || "",
    accountMedicalAidNumber: patient.patientMedicalAidNumber || "",
    accountMedicalAidStatus: patient.patientMedicalAidStatus || "",
    accountMedicalAidJoinDate: patient.patientMedicalAidJoinDate || "",
    accountIODFund: patient.patientIODFund || "",
    accountIODFundCode: patient.patientIODFundCode || "",
    accountIODFundCategory: patient.patientIODFundCategory || "",
    accountIODPlan: patient.patientIODPlan || "",
    accountIODPlanCode: patient.patientIODPlanCode || "",
    accountIODOption: patient.patientIODOption || "",
    accountIODOptionCode: patient.patientIODOptionCode || "",
    accountIODEmployer: patient.patientIODEmployer || "",
    accountIODClaimRefNumber: patient.patientIODClaimRefNumber || "",
    accountIODEmployerWCARegNum: patient.patientIODEmployerWCARegNum || "",
    accountIODDateOfInjury: patient.patientIODDateOfInjury || "",
  };
}

function handleChangeOrphanAccount(e) {
  setOrphanAccountData((prev) => ({
    ...prev,
    [e.target.name]: e.target.value,
  }));
}

function computeAllDiffs() {
  const origPatient = originalPatientRef.current;
  const origAccount = originalAccountRef.current;
  if (!origPatient) return { patientDiff: [], accountDiff: [], identityMismatch: false };
  const patientDiff = computeFieldDiff(origPatient, patient, patientDiffFields);
  const accountDiff = origAccount
    ? computeFieldDiff(origAccount, account, accountDiffFields)
    : [];

  // 9.1 Automatic matching — flag when identity fields changed significantly
  const idMatch = String(origPatient.patientIdNumber || "") === String(patient.patientIdNumber || "");
  const dobMatch = String(origPatient.patientDOB || "") === String(patient.patientDOB || "");
  const nameMatch =
    (origPatient.patientFirstName || "").trim().toLowerCase() ===
      (patient.patientFirstName || "").trim().toLowerCase() &&
    (origPatient.patientLastName || "").trim().toLowerCase() ===
      (patient.patientLastName || "").trim().toLowerCase();
  const identityMismatch = !idMatch && !dobMatch && !nameMatch;

  return { patientDiff, accountDiff, identityMismatch };
}

async function handleSaveClick() {
  // ── Main-member validation (runs on ALL steps, including confirm) ───────
  // Skip when promoting or moving to private account — patient is leaving the old account entirely
  if (account?._id && moveMode !== "PROMOTE" && moveMode !== "PRIVATE_ACCOUNT") {
    setIsLoading(true);
    let accountPatients = [];
    try {
      const result = await getAccountPatients(account._id);
      accountPatients = Array.isArray(result) ? result : [];
    } catch (e) {
      console.error("Could not fetch account patients for validation:", e);
    }
    setIsLoading(false);

    const otherPatients = accountPatients.filter(
      (p) => String(p._id) !== String(patient._id)
    );

    // Cannot make this patient main member if another already is
    if (
      patient.patientMainMember &&
      !originalPatientRef.current?.patientMainMember
    ) {
      const conflictPatient = otherPatients.find((p) => p.patientMainMember);
      if (conflictPatient) {
        showToast(
          "error",
          `${conflictPatient.patientFirstName} ${conflictPatient.patientLastName} is already the main member on this account. Please remove their main member status first before making this patient the main member.`
        );
        return;
      }
    }
  }

  // Cannot move a main member to an account that already has one
  if (
    moveMode === "MOVE_EXISTING" &&
    selectedAccount?._id &&
    patient.patientMainMember
  ) {
    setIsLoading(true);
    let destPatients = [];
    try {
      const result = await getAccountPatients(selectedAccount._id);
      destPatients = Array.isArray(result) ? result : [];
    } catch (e) {
      console.error("Could not fetch destination account patients:", e);
    }
    setIsLoading(false);

    const destMainMember = destPatients.find((p) => p.patientMainMember);
    if (destMainMember) {
      showToast(
        "error",
        `The destination account already has a main member (${destMainMember.patientFirstName} ${destMainMember.patientLastName}). Please first remove their main member status before moving this patient as the main member.`
      );
      return;
    }
  }
  // ────────────────────────────────────────────────────────────────────────

  if (modalStep === "edit" || modalStep === "account") {
    // If on edit step and creating orphan account, go to account step first
    if (modalStep === "edit" && createOrphanAccount && !account) {
      setOrphanAccountData(buildOrphanAccountData());
      setModalStep("account");
      return;
    }

    // Compute diff and go to confirm; always show confirm when there are
    // action-level changes (move, removal, orphan account) even if no field diffs
    const diffs = computeAllDiffs();
    const hasActionChanges =
      moveMode !== "NONE" ||
      removeMed ||
      removeMedBulk ||
      (createOrphanAccount && !account);
    if (
      diffs.patientDiff.length === 0 &&
      diffs.accountDiff.length === 0 &&
      !hasActionChanges
    ) {
      executeSave();
    } else {
      setChangesDiff(diffs);
      setModalStep("confirm");
    }
  } else if (modalStep === "confirm") {
    executeSave();
  }
}

async function executeSave() {
  setModalStep("edit");
  setIsLoading(true);
  try {
    // ✅ Always persist any normal patient edits first (including IOD fields)
    console.log("💾 Saving patient:", patient);
    await updatePatient(patient._id, patient);

    // ✅ Sync phone number to all appointments for this patient
    if (patient.patientCellNumber) {
      try {
        await syncPatientPhone(patient._id, patient.patientCellNumber);
      } catch (e) {
        console.warn("Could not sync phone to appointments:", e);
      }
    }

    // ✅ If IOD patient, sync IOD fields to the account + clear regular med aid fields
    if (isIOD && account?._id) {
      await updateAccount(account._id, {
        ...account,
        // Set IOD fields on account
        accountIODFund: patient.patientIODFund || "",
        accountIODFundCode: patient.patientIODFundCode || "",
        accountIODFundCategory: patient.patientIODFundCategory || "",
        accountIODPlan: patient.patientIODPlan || "",
        accountIODPlanCode: patient.patientIODPlanCode || "",
        accountIODOption: patient.patientIODOption || "",
        accountIODOptionCode: patient.patientIODOptionCode || "",
        accountIODEmployer: patient.patientIODEmployer || "",
        accountIODClaimRefNumber: patient.patientIODClaimRefNumber || "",
        accountIODEmployerWCARegNum: patient.patientIODEmployerWCARegNum || "",
        accountIODDateOfInjury: patient.patientIODDateOfInjury || "",
        // Spec: IOD strict separation — clear regular med aid on account
        ...clearAccountMedicalAid,
      });
    }

    // ✅ If reverting from IOD to medical aid, clear IOD fields on account
    if (revertFromIOD && account?._id) {
      await updateAccount(account._id, {
        ...account,
        accountIODFund: "",
        accountIODFundCode: "",
        accountIODPlan: "",
        accountIODPlanCode: "",
        accountIODOption: "",
        accountIODOptionCode: "",
        accountIODEmployer: "",
        accountIODClaimRefNumber: "",
        accountIODEmployerWCARegNum: "",
        accountIODDateOfInjury: "",
      });
    }

    // If we have an account and we are removing OR updating fund info -> use bulk update
    const accountId = account?._id;
    const patientIds = accountId
      ? getAccountPatientIds(account)
      : [patient._id];

    const shouldBulkRemove = !!accountId && (removeMed || removeMedBulk);
    const shouldBulkUpdateFund =
      !!accountId && !!mainMemberPerson && !shouldBulkRemove;

    if (shouldBulkRemove) {
      const payload = buildClearMedicalAidPayload();
      await bulkUpdatePatients(
        patientIds,
        account._id,
        payload,
        dependantList
      );
    }

    if (shouldBulkUpdateFund) {
      const payload = {
        patientPreviousMedicalAid: prevAid,
        dependantList: dependantList || [],
        ...makePatientBlock(mainMemberPerson, patient, patient, prevAid),
        ...makeAccountBlock(mainMemberPerson, patient, account),
      };

      await bulkUpdatePatients(patientIds, account._id, payload, dependantList);
    }

    // Move flows
    if (moveMode === "MOVE_EXISTING" && selectedAccount?._id) {
      const updatedMedicalAidFields = movePatientBlock(selectedAccount, prevAid);
      // Only remove from old account if one exists (orphaned patients won't have one)
      if (account?._id) {
        await removePatientFromAccount(account._id, patient._id);
      }
      await addPatientToAccount(selectedAccount._id, patient._id);
      await updatePatient(patient._id, { ...patient, ...updatedMedicalAidFields });
    }

    if (moveMode === "CREATE_NEW" && mainMemberPerson) {
      // Remove patient from old account before creating new one
      if (account?._id) {
        await removePatientFromAccount(account._id, patient._id);
      }
      const practiceNumbers = Array.isArray(patient.patientPractice)
        ? patient.patientPractice
        : [patient.patientPractice];
      const numResult = await generatePatAccNumber(practiceNumbers);
      const accountNumber = numResult?.accountNumber || "";
      const newAccPayload = newAccount(mainMemberPerson, patient);
      await createAccount({ ...newAccPayload, accountNumber });
    }

    if (moveMode === "PROMOTE") {
      // Remove patient from old account
      if (account?._id) {
        await removePatientFromAccount(account._id, patient._id);
      }
      // Create a new account for this patient using their personal data (no medical aid/IOD)
      const practiceNumbers = Array.isArray(patient.patientPractice)
        ? patient.patientPractice
        : [patient.patientPractice];
      const numResult = await generatePatAccNumber(practiceNumbers);
      const accountNumber = numResult?.accountNumber || "";
      await createAccount({
        accountNumber,
        accountTitle: patient.patientTitle || "",
        accountFullNames: patient.patientFullNames || "",
        accountFirstName: patient.patientFirstName || "",
        accountLastName: patient.patientLastName || "",
        accountInitials: patient.patientInitials || "",
        accountIdNumber: patient.patientIdNumber || "",
        accountDOB: patient.patientDOB || "",
        accountEmail: patient.patientEmail || "",
        accountTelNumberCell: patient.patientCellNumber || "",
        accountPractice: patient.patientPractice,
        accountPatientId: patient._id,
      });
    }

    if (moveMode === "PRIVATE_ACCOUNT") {
      // Remove patient from old account
      if (account?._id) {
        await removePatientFromAccount(account._id, patient._id);
      }
      // Create a new private account (no medical aid) for this patient
      const practiceNumbers = Array.isArray(patient.patientPractice)
        ? patient.patientPractice
        : [patient.patientPractice];
      const numResult = await generatePatAccNumber(practiceNumbers);
      const accountNumber = numResult?.accountNumber || "";
      await createAccount({
        accountNumber,
        accountTitle: patient.patientTitle || "",
        accountFullNames: patient.patientFullNames || "",
        accountFirstName: patient.patientFirstName || "",
        accountLastName: patient.patientLastName || "",
        accountInitials: patient.patientInitials || "",
        accountIdNumber: patient.patientIdNumber || "",
        accountDOB: patient.patientDOB || "",
        accountEmail: patient.patientEmail || "",
        accountTelNumberCell: patient.patientCellNumber || "",
        accountPractice: patient.patientPractice,
        accountPatientId: patient._id,
      });
    }

    // Create account for orphaned patient (using user-reviewed orphanAccountData)
    if (createOrphanAccount && !account && orphanAccountData) {
      const practiceNumbers = Array.isArray(patient.patientPractice)
        ? patient.patientPractice
        : [patient.patientPractice];
      const numResult = await generatePatAccNumber(practiceNumbers);
      const accountNumber = numResult?.accountNumber || "";

      await createAccount({
        ...orphanAccountData,
        accountNumber,
        accountPatientId: patient._id,
      });
    }

    // ✅ Fallback: persist account changes that weren't handled by existing paths
    // (e.g. __manualApply sets account medical aid fields in state but no bulk/IOD path saves them)
    const accountChanged =
      account?._id &&
      originalAccountRef.current &&
      !shouldBulkRemove &&
      !shouldBulkUpdateFund &&
      !(isIOD && account?._id) &&
      !revertFromIOD &&
      moveMode !== "MOVE_EXISTING" &&
      moveMode !== "PROMOTE" &&
      moveMode !== "PRIVATE_ACCOUNT" &&
      JSON.stringify(account) !== JSON.stringify(originalAccountRef.current);

    if (accountChanged) {
      await updateAccount(account._id, account);
    }

    showToast("success", "Patient information updated successfully.");
    setTimeout(() => {
      onHide();
      if (onSave) onSave();
    }, 1500);
  } catch (err) {
    console.error("❌ Error saving patient:", err);
    showToast("error", `Error saving patient: ${err?.response?.data?.message || err.message || "Unknown error"}`);
    // On a number conflict, clear the patient number so the user can enter a different one
    if (err?.response?.status === 409) {
      setPatient((prev) => ({ ...prev, patientNumber: "" }));
    }
  } finally {
    setIsLoading(false);
  }
}

  const handleClose = () => {
    onHide(); // Close the modal in the parent first
    setNotFoundAlert(false);
    setNotFoundMMAlert(false);
    setMainMemberWarning(false);
    setRemoveMed(false);
    setRemoveMedBulk(false);
    setNewMainMemberWarning(false);
    setNoMainMemberAlert(false);
    setBlocked(false);
    setPatient(null);
    setExistingAccount(false);
    setAccount(null);
    setDependantList(null);
    setImportInfo(false);
    setSearchNewAccount(false);
    setSelectedAccount(null);
    setSelectedDependant(null);
    setIdMismatchAlert(false);
    setConvertToIOD(false);
    setRevertFromIOD(false);
    setCreateOrphanAccount(false);
    setShowRemoveMedChoice(false);
    setModalStep("edit");
    setOrphanAccountData(null);
  };

 const isIOD = (Boolean(patient?.patientIODFund) || convertToIOD) && !revertFromIOD;

  console.log("Is IOD Patient:", isIOD);

  // Rendering the content of the forms based on the current step
  const renderContent = () => {
    return (
      <Form>
        {searchNewAccount && !selectedAccount && (
          <Row className="mb-1">
            <p className="text-primary">
              NOTE: If you can't find the account please create the account
              patient with it's account first via the add patient function.
            </p>
          </Row>
        )}
        {selectedAccount && searchNewAccount && !importInfo && (
          <>
            <Row className="mb-1">
              <strong className="text-primary">Selected Account:</strong>
            </Row>
            <Row className="mb-0">
              <Col md={4}>
                <Form.Label className="fw-normal">
                  {selectedAccount.accountTitle}{" "}
                  {selectedAccount.accountFullNames}{" "}
                  {selectedAccount.accountLastName} (
                  {selectedAccount.accountFirstName})
                </Form.Label>
              </Col>
              <Col md={6}></Col>
            </Row>

            {/* Three Equal Columns */}
            <Row>
              {/* Column 1: Personal Details */}
              <Col md={4}>
                {selectedAccount.accountIdNumber && (
                  <>
                    <i className="fa-solid fa-id-card me-1 text-primary"></i>{" "}
                    {selectedAccount.accountIdNumber} <br />
                  </>
                )}
                {selectedAccount.accountCellNumber && (
                  <>
                    <i className="fas fa-phone-alt me-2 text-primary"></i>{" "}
                    {selectedAccount.accountCellNumber} <br />
                  </>
                )}
                {selectedAccount.accountEmail && (
                  <>
                    <i className="fas fa-envelope me-2 text-primary"></i>{" "}
                    {selectedAccount.accountEmail} <br />
                  </>
                )}
              </Col>
              <Col md={6}>
                {selectedAccount.accountMedicalAid ? (
                  <>
                    <i className="fa fa-notes-medical me-2 text-primary"></i>{" "}
                    {selectedAccount.accountMedicalAid} <br />
                    <i className="fa fa-notes-medical me-2 text-white"></i>{" "}
                    {selectedAccount.accountMedicalAidPlan} |{" "}
                    {selectedAccount.accountMedicalAidOption} <br />
                    <i className="fa fa-notes-medical me-2 text-white"></i>{" "}
                    {selectedAccount.accountMedicalAidNumber} <br />
                  </>
                ) : (
                  <p className="text-primary">
                    <i className="fa fa-notes-medical me-2 text-primary"></i>{" "}
                    This is a Private account
                  </p>
                )}
              </Col>
            </Row>
            <hr />
          </>
        )}
        <Row className="mb-3 mt-3">
          <Col md={3}>
            <Form.Group as={Col} controlId="formGridPatientnumber">
              <Form.Label>Patient Number</Form.Label>
              <Form.Control
                required
                className="w-75"
                placeholder="Custom Patient Number"
                name="patientNumber"
                value={patient?.patientNumber}
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group as={Col} controlId="formGridDependantCode">
              <Form.Label>Dependant Code</Form.Label>
              <Form.Control
                className={`w-75${highlightManualFields ? " field-highlight" : ""}`}
                placeholder="Dependant Code"
                name="patientMedicalAidDependantCode"
                value={patient?.patientMedicalAidDependantCode}
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={3} className="d-flex align-items-end pb-1">
            <div className="form-check">
              <input
                type="checkbox"
                id="formGridMainMember"
                className={`form-check-input${highlightManualFields ? " field-highlight" : ""}`}
                checked={!!patient?.patientMainMember}
                onChange={(e) =>
                  setPatient((prev) => ({
                    ...prev,
                    patientMainMember: e.target.checked,
                  }))
                }
              />
              <label htmlFor="formGridMainMember" className="form-check-label">
                Main Member
              </label>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={2}>
            <Form.Group as={Col} controlId="formGridTitle">
              <Form.Label>Title</Form.Label>
              <Form.Select
                required
                defaultValue=""
                name="patientTitle"
                value={patient?.patientTitle}
                onChange={handleChangePatient}
              >
                <option></option>
                <option>Ms</option>
                <option>Mr</option>
                <option>Mrs</option>
                <option>Dr</option>
                <option>Prof</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group as={Col} controlId="formGridFullNames">
              <Form.Label>Full Name(s)</Form.Label>
              <Form.Control
                required
                name="patientFullNames"
                value={patient?.patientFullNames}
                readOnly={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                }
                className={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                    ? "bg-light"
                    : ""
                }
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group as={Col} controlId="formGridLastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                required
                name="patientLastName"
                value={patient?.patientLastName}
                readOnly={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                }
                className={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                    ? "bg-light"
                    : ""
                }
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group as={Col} controlId="formGridInitials">
              <Form.Label>Initials</Form.Label>
              <Form.Control
                name="patientInitials"
                value={patient?.patientInitials}
                readOnly={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                }
                className={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                    ? "bg-light"
                    : ""
                }
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group as={Col} controlId="formGridFirstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                required
                value={patient?.patientFirstName}
                readOnly={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                }
                className={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                    ? "bg-light"
                    : ""
                }
                name="patientFirstName"
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group as={Col} controlId="formGridDOB">
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                required
                type="date"
                value={patient?.patientDOB}
                readOnly={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                }
                className={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                    ? "bg-light"
                    : ""
                }
                name="patientDOB"
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group as={Col} controlId="formGridIdNumber">
              <Form.Label>ID Number</Form.Label>
              <Form.Control
                name="patientIdNumber"
                value={patient?.patientIdNumber}
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group as={Col} controlId="formGridCellPhone">
              <Form.Label>Mobile Number</Form.Label>
              <Form.Control
                required
                value={patient?.patientCellNumber}
                name="patientCellNumber"
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group as={Col} controlId="formGridEmailAddress">
              <Form.Label>Email</Form.Label>
              <Form.Control
                required
                value={patient?.patientEmail}
                name="patientEmail"
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group as={Col} controlId="formGridTitle">
              <Form.Label>Gender</Form.Label>
              <Form.Select
                required
                value={patient?.patientGender}
                readOnly={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                }
                disabled={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                }
                className={
                  patient?.patientDataSource === "familyCheck" ? "bg-light" : ""
                }
                name="patientGender"
                onChange={(e) =>
                  setPatient((prev) => ({
                    ...prev,
                    patientGender: e.target.value,
                  }))
                }
              >
                <option value="">Select Gender</option>
                <option value="2">Female</option>
                <option value="1">Male</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {isSysAdmin && (
          <>
            <hr />
            <Row className="mb-1 align-items-center">
              <Col>
                <span className="text-muted fw-semibold" style={{ fontSize: "0.8rem" }}>
                  MEDIKREDIT PLAN NUMBERS
                </span>
              </Col>
              <Col xs="auto">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-secondary"
                  title="View Medical Aids reference table"
                  onClick={() => setShowMedAidRef(true)}
                >
                  <i className="fa-solid fa-circle-info" />
                </Button>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="formGridMK03">
                  <Form.Label style={{ fontSize: "0.82rem" }}>Plan Number 03</Form.Label>
                  <Form.Control
                    size="sm"
                    name="patientMediKreditPlanNumber03"
                    value={patient?.patientMediKreditPlanNumber03 || ""}
                    onChange={handleChangePatient}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="formGridMK04">
                  <Form.Label style={{ fontSize: "0.82rem" }}>Plan Number 04</Form.Label>
                  <Form.Control
                    size="sm"
                    name="patientMediKreditPlanNumber04"
                    value={patient?.patientMediKreditPlanNumber04 || ""}
                    onChange={handleChangePatient}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="formGridMK06">
                  <Form.Label style={{ fontSize: "0.82rem" }}>Plan Number 06</Form.Label>
                  <Form.Control
                    size="sm"
                    name="patientMediKreditPlanNumber06"
                    value={patient?.patientMediKreditPlanNumber06 || ""}
                    onChange={handleChangePatient}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}
      </Form>
    );
  };

  return (
    <>
      <Modal
        show={show}
        //size="xl"
        centered
        aria-labelledby="contained-modal-title-vcenter"
        onHide={handleClose}
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton className="border-secondary">
          <div className="w-100">
            <Modal.Title
              id="contained-modal-title-vcenter"
              className="text-primary fs-5"
            >
              {modalStep === "edit" && "Update Patient Information"}
              {modalStep === "account" && "Review Account Details"}
              {modalStep === "confirm" && "Confirm Changes"}
              <Row className="fw-normal fs-6 fw-light">
                <Form.Text>
                  {modalStep === "edit" && "Please enter the patient information in the form below."}
                  {modalStep === "account" && (
                    <>Step 2 of 3 &mdash; The following account will be created for this patient. Review and edit the details.</>
                  )}
                  {modalStep === "confirm" && (
                    <>Step {createOrphanAccount && !account ? "3 of 3" : "2 of 2"} &mdash; Review the changes below before saving.</>
                  )}
                </Form.Text>
              </Row>
            </Modal.Title>
          </div>
        </Modal.Header>

        {/* Alerts only show on the edit step */}
        {modalStep === "edit" && (
          <>
        {notFoundMMAlert && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setNotFoundMMAlert(false)}
          >
            <span>
              This main member's online medical aid record couldn't be
              retrieved, this could be due to it either not being available from
              the fund or a connection issue. Any changes made on this patient
              will be applied to all it's dependants.
            </span>
          </Alert>
        )}
        {noExistingAccountAlert && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setNoExistingAccountAlert(false)}
          >
            <span>
              The main member for the selected dependant was not found in
              PractoSync. Please first create the main member's patient and
              Account before attempting to move the dependant to their account.
            </span>
          </Alert>
        )}
        {selectedAccount?.accountMedicalAid && (
          <Alert
            variant="info"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            dismissible
          >
            <span>
              The selected account is currently on Medical Aid, if this is still
              correct consider importing the dependant via the Import Fund
              function.
            </span>
          </Alert>
        )}
        {mainMemberWarning && !removeMedBulk && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setMainMemberWarning(false)}
          >
            <span>
              You are updating the main member's information. The changes to
              Medical Aid information will be applied to all dependants.
            </span>
          </Alert>
        )}
        {newMainMemberWarning && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setNewMainMemberWarning(false)}
          >
            <span>
              The selected dependant was not registered as a main member before.
              They will now be the main member on their own account. Any
              outstanding invoices will not be transferred to the new account.
            </span>
          </Alert>
        )}
        {newDependantWarning && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setNewMainMemberWarning(false)}
          >
            <span>
              The selected dependant was a main member before. The dependant
              will be moved to the new account with all their existing
              dependants. Any outstanding invoices will not be transferred to
              the new account.
            </span>
          </Alert>
        )}
        {removeMed && (
          <Alert
            variant="danger"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setRemoveMed(false)}
          >
            <span>
              NOTE: You are about to remove the medical aid information from
              this patient. If you submit all claims still in process with the
              medical aid will remain with the medical aid until you reverse
              them.
            </span>
          </Alert>
        )}
        {removeMedBulk && (
          <Alert
            variant="danger"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setRemoveMedBulk(false)}
          >
            <span>
              NOTE: You are about to remove the medical aid information from
              this account. The medical aid information will also be removed
              from all it's dependants. If you submit all claims still in
              process with the medical aid will remain with the medical aid
              until you reverse them.
            </span>
          </Alert>
        )}
        {idMismatchAlert && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setIdMismatchAlert(false)}
          >
            <span>
              NOTE: Either the last name or the ID number of the patient does
              not match the existing records. Please verify the information
              before proceeding.
            </span>
          </Alert>
        )}
          </>
        )}


        <Modal.Body>
        {modalStep === "edit" && (
          <Row className="g-3">
            {/* Left column: always‑visible buttons */}
            <Col
              md={3}
              className="sidebar d-flex flex-column border-end border-1 pe-3"
            >
              <Button
                variant="primary"
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={() => setImportInfo(true)}
              >
                <i className={`fa-solid ${isIOD ? 'fa-pen-to-square' : 'fa-file-import'} me-2 ms-2`} />
                <span className="btn-text">{isIOD ? 'Update IOD' : 'Import Fund'}</span>
              </Button>

{!isIOD && (
  <>
              <Button
                variant="primary"
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={() => {
                  setConvertToIOD(true);
                  setRevertFromIOD(false);
                  setImportInfo(true);
                }}
              >
                <i className="fa-solid fa-briefcase-medical me-2 ms-2" />
                <span className="btn-text">Convert to IOD / Insurance</span>
              </Button>

              <Button
                variant="primary"
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={handleRemove}
              >
                <i className="fa-solid fa-trash me-2 ms-2" />
                <span className="btn-text">Remove Medical Aid</span>
              </Button>

              {!patient?.patientMainMember && (
              <Button
                variant="primary"
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={onClickPromoteMainMember}
              >
                <i className="fa-solid fa-user-check me-2 ms-2" />
                <span className="btn-text">Promote to Main Member</span>
              </Button>
              )}

              <Button
                variant="primary"
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={() => setSearchNewAccount(true)}
              >
                <i className="fa-solid fa-arrow-right-to-bracket me-2 ms-2" />
                <span className="btn-text">Move to New Account</span>
              </Button>
              </>
              )}

{isIOD && (
              <>
              <Button
                variant="outline-primary"
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={() => {
                  setConvertToIOD(false);
                  setRevertFromIOD(true);
                  setImportInfo(true);
                }}
              >
                <i className="fa-solid fa-file-import me-2 ms-2" />
                <span className="btn-text">Revert to Medical Aid</span>
              </Button>
              <Button
                variant="outline-secondary"
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={() => {
                  setConvertToIOD(false);
                  setRevertFromIOD(true);
                  setPatient((prev) => ({
                    ...prev,
                    patientIODFund: "",
                    patientIODFundCode: "",
                    patientIODFundCategory: "",
                    patientIODPlan: "",
                    patientIODPlanCode: "",
                    patientIODOption: "",
                    patientIODOptionCode: "",
                    patientIODEmployer: "",
                    patientIODEmployerWCARegNum: "",
                    patientIODDateOfInjury: "",
                    patientIODClaimRefNumber: "",
                    patientIODPatient: false,
                  }));
                  setUpdated(true);
                }}
              >
                <i className="fa-solid fa-user-slash me-2 ms-2" />
                <span className="btn-text">Revert to Private</span>
              </Button>
              </>
              )}

              {/* Create Account for orphaned patients */}
              {!account && (
              <Button
                variant={createOrphanAccount ? "success" : "danger"}
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={() => setCreateOrphanAccount(true)}
                disabled={createOrphanAccount}
              >
                <i className={`fa-solid ${createOrphanAccount ? 'fa-check' : 'fa-plus'} me-2 ms-2`} />
                <span className="btn-text">
                  {createOrphanAccount ? "Account Will Be Created" : "Create Account"}
                </span>
              </Button>
              )}
            </Col>

            {/* Right column: patient info + conditional content */}
            <Col md={9} className="info-col">
              {/* Patient header info */}
              <Row className="mb-3">
                <Col md={4}>
                  <strong>
                    {patient?.patientTitle} {patient?.patientInitials}{" "}
                    {patient?.patientFirstName} {patient?.patientLastName}
                  </strong>
                </Col>

                <Col md={4}>
                  {patient?.patientMedicalAid && (
                    <>
                      <p className="mb-1">{patient?.patientMedicalAid}</p>
                      <p className="mb-1">
                        {patient?.patientMedicalAidPlan} |{" "}
                        {patient?.patientMedicalAidOption}
                      </p>
                      <p className="mb-1">
                        {patient?.patientMedicalAidNumber} (
                        {patient?.patientMedicalAidDependantCode})
                      </p>
                    </>
                  )}
                </Col>

                <Col md={4}>
                  {patient?.patientMedicalAidStatus && (
                    <>
                      <p
                        className={
                          patient?.patientMedicalAidStatus
                            .toLowerCase()
                            .includes("active")
                            ? "text-success fw-bold"
                            : "text-danger fw-bold"
                        }
                      >
                        {patient?.patientMedicalAidStatus}
                        {patient?.patientMedicalAidJoinDate && (
                          <span className="fw-normal text-muted ms-2">
                            (Joined: {patient?.patientMedicalAidJoinDate})
                          </span>
                        )}
                      </p>
                    </>
                  )}
                </Col>
              </Row>

              {/* Import fund flow */}
              {importInfo && (
                <Row className="mb-3">
                  <Col md={12}>
                    <SearchMedAidPatient
                      onDependantListUpdate={handleUpdateDependantList}
                      addPatientSteps={applyFamilySelection}
                      patientAccount={account} // ✅ needed for existing-account flow
                      accountExisting={!!account} // ✅ tells Search that scheme/number may be prefilled
                      iodPatient={isIOD} 
                      editPatient={patient}
                    />
                  </Col>
                </Row>
              )}

              {/* Remove medical aid choice flow */}
              {showRemoveMedChoice && (
                <Row className="mb-3">
                  <Col md={12}>
                    <Alert variant="warning" className="d-flex align-items-center justify-content-between py-2">
                      <span>
                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                        How would you like to remove the medical aid?
                      </span>
                      <div className="d-flex flex-wrap gap-2 ms-3">
                        <Button variant="danger" size="sm" onClick={handleRemoveMedAll}>
                          <i className="fa-solid fa-users me-2"></i>
                          Remove from entire account
                        </Button>
                        <Button variant="primary" size="sm" onClick={handleRemoveMedPrivate}>
                          <i className="fa-solid fa-user-plus me-2"></i>
                          Create private account for this patient
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => setShowRemoveMedChoice(false)}>
                          Cancel
                        </Button>
                      </div>
                    </Alert>
                  </Col>
                </Row>
              )}

              {/* Move‑to‑account flow */}
              {searchNewAccount && !importInfo && (
                <Row className="mb-3">
                  <Col md={10}>
                    <div className="d-flex">
                      <div className="flex-grow-1 me-1">
                        {" "}
                        {/* Small margin-end for minimal spacing */}
                        <AccountSearch
                          onAccountSelect={handleAccountSearchSelect}
                        />
                      </div>
                      <Button
                        className="d-flex align-content-center justify-content-center"
                        variant=""
                        size="lg"
                        onClick={() => {
                          setSearchNewAccount(false);
                          setSelectedAccount("");
                        }}
                      >
                        <i className="fa-solid fa-trash text-danger" />
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}

              <hr className="mt-0 mb-3" />

              {highlightManualFields && (
                <style>{`
                  @keyframes field-highlight-pulse {
                    0%   { box-shadow: 0 0 0 0 rgba(13,110,253,0.5); }
                    70%  { box-shadow: 0 0 0 6px rgba(13,110,253,0); }
                    100% { box-shadow: 0 0 0 0 rgba(13,110,253,0); }
                  }
                  .field-highlight { animation: field-highlight-pulse 1.2s ease-out 3; }
                `}</style>
              )}
              <div className="step-content">{renderContent()}</div>
            </Col>
          </Row>
        )}

        {/* Step: Account — orphan account fields form */}
        {modalStep === "account" && orphanAccountData && (
          <Form>
            <Row className="mb-3">
              <Col md={2}>
                <Form.Group controlId="orphanTitle">
                  <Form.Label>Title</Form.Label>
                  <Form.Select name="accountTitle" value={orphanAccountData.accountTitle} onChange={handleChangeOrphanAccount}>
                    <option></option>
                    <option>Ms</option>
                    <option>Mr</option>
                    <option>Mrs</option>
                    <option>Dr</option>
                    <option>Prof</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="orphanFullNames">
                  <Form.Label>Full Name(s)</Form.Label>
                  <Form.Control name="accountFullNames" value={orphanAccountData.accountFullNames} onChange={handleChangeOrphanAccount} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="orphanLastName">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control name="accountLastName" value={orphanAccountData.accountLastName} onChange={handleChangeOrphanAccount} />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId="orphanInitials">
                  <Form.Label>Initials</Form.Label>
                  <Form.Control name="accountInitials" value={orphanAccountData.accountInitials} onChange={handleChangeOrphanAccount} />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="orphanFirstName">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control name="accountFirstName" value={orphanAccountData.accountFirstName} onChange={handleChangeOrphanAccount} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="orphanDOB">
                  <Form.Label>Date of Birth</Form.Label>
                  <Form.Control type="date" name="accountDOB" value={orphanAccountData.accountDOB} onChange={handleChangeOrphanAccount} />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group controlId="orphanIdNumber">
                  <Form.Label>ID Number</Form.Label>
                  <Form.Control name="accountIdNumber" value={orphanAccountData.accountIdNumber} onChange={handleChangeOrphanAccount} />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="orphanCell">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control name="accountTelNumberCell" value={orphanAccountData.accountTelNumberCell} onChange={handleChangeOrphanAccount} />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group controlId="orphanEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control name="accountEmail" value={orphanAccountData.accountEmail} onChange={handleChangeOrphanAccount} />
                </Form.Group>
              </Col>
            </Row>
            <hr />
            {!isIOD && (
              <>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group controlId="orphanMedAid">
                      <Form.Label>Medical Aid</Form.Label>
                      <Form.Control name="accountMedicalAid" value={orphanAccountData.accountMedicalAid} onChange={handleChangeOrphanAccount} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="orphanPlan">
                      <Form.Label>Plan</Form.Label>
                      <Form.Control name="accountMedicalAidPlan" value={orphanAccountData.accountMedicalAidPlan} onChange={handleChangeOrphanAccount} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="orphanOption">
                      <Form.Label>Option</Form.Label>
                      <Form.Control name="accountMedicalAidOption" value={orphanAccountData.accountMedicalAidOption} onChange={handleChangeOrphanAccount} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group controlId="orphanMedAidNumber">
                      <Form.Label>Medical Aid Number</Form.Label>
                      <Form.Control name="accountMedicalAidNumber" value={orphanAccountData.accountMedicalAidNumber} onChange={handleChangeOrphanAccount} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="orphanMedAidStatus">
                      <Form.Label>Status</Form.Label>
                      <Form.Control name="accountMedicalAidStatus" value={orphanAccountData.accountMedicalAidStatus} onChange={handleChangeOrphanAccount} />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
            {isIOD && (
              <>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group controlId="orphanIODFund">
                      <Form.Label>IOD Fund</Form.Label>
                      <Form.Control name="accountIODFund" value={orphanAccountData.accountIODFund} onChange={handleChangeOrphanAccount} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="orphanIODPlan">
                      <Form.Label>IOD Plan</Form.Label>
                      <Form.Control name="accountIODPlan" value={orphanAccountData.accountIODPlan} onChange={handleChangeOrphanAccount} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="orphanIODOption">
                      <Form.Label>IOD Option</Form.Label>
                      <Form.Control name="accountIODOption" value={orphanAccountData.accountIODOption} onChange={handleChangeOrphanAccount} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group controlId="orphanIODEmployer">
                      <Form.Label>IOD Employer</Form.Label>
                      <Form.Control name="accountIODEmployer" value={orphanAccountData.accountIODEmployer} onChange={handleChangeOrphanAccount} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="orphanIODClaimRef">
                      <Form.Label>Claim Ref Number</Form.Label>
                      <Form.Control name="accountIODClaimRefNumber" value={orphanAccountData.accountIODClaimRefNumber} onChange={handleChangeOrphanAccount} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="orphanIODDateOfInjury">
                      <Form.Label>Date of Injury</Form.Label>
                      <Form.Control type="date" name="accountIODDateOfInjury" value={orphanAccountData.accountIODDateOfInjury} onChange={handleChangeOrphanAccount} />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </Form>
        )}

        {/* Step: Confirm — diff tables */}
        {modalStep === "confirm" && (
          <>
            {changesDiff.identityMismatch && (
              <Alert variant="danger" className="d-flex align-items-center">
                <i className="fa-solid fa-triangle-exclamation me-2 fs-5"></i>
                <div>
                  <strong>Significant identity change detected!</strong><br />
                  The patient's ID number, date of birth, and name do not match the original record.
                  Please verify this is the correct patient before confirming.
                </div>
              </Alert>
            )}

            {/* Action summaries */}
            {(moveMode !== "NONE" || removeMedBulk || (createOrphanAccount && !account)) && (
              <>
                <h6 className="text-primary mb-2">
                  <i className="fa-solid fa-bolt me-2"></i>Actions
                </h6>
                <table className="table table-bordered table-sm mb-4">
                  <tbody>
                    {moveMode === "MOVE_EXISTING" && selectedAccount && (
                      <tr>
                        <td className="fw-bold">Move to Account</td>
                        <td>
                          {selectedAccount.accountFirstName} {selectedAccount.accountLastName}
                          {selectedAccount.accountNumber ? ` (${selectedAccount.accountNumber})` : ""}
                          {selectedAccount.accountMedicalAid ? ` — ${selectedAccount.accountMedicalAid}` : " — Private"}
                        </td>
                      </tr>
                    )}
                    {moveMode === "CREATE_NEW" && (
                      <tr>
                        <td className="fw-bold">Promote to Main Member</td>
                        <td>Patient will be moved to a new account as main member (via Family Check).</td>
                      </tr>
                    )}
                    {moveMode === "PROMOTE" && (
                      <tr className="table-warning">
                        <td className="fw-bold">Promote to Main Member</td>
                        <td>
                          Patient will be removed from their current account. All medical aid and
                          IOD information will be cleared. A new account will be created for them
                          as the main member.
                        </td>
                      </tr>
                    )}
                    {moveMode === "PRIVATE_ACCOUNT" && (
                      <tr className="table-warning">
                        <td className="fw-bold">Move to Private Account</td>
                        <td>
                          Patient will be removed from their current account and their medical aid
                          information will be cleared. A new private account will be created for
                          them as the main member.
                        </td>
                      </tr>
                    )}
                    {(removeMed || removeMedBulk) && (
                      <tr className="table-danger">
                        <td className="fw-bold">Remove Medical Aid</td>
                        <td>
                          {removeMedBulk
                            ? "Medical aid information will be removed from this patient and all dependants on the account."
                            : "Medical aid information will be removed from this patient."}
                        </td>
                      </tr>
                    )}
                    {createOrphanAccount && !account && (
                      <tr className="table-success">
                        <td className="fw-bold">Create Account</td>
                        <td>A new account will be created for this patient.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}

            {changesDiff.patientDiff.length > 0 && (
              <>
                <h6 className="text-primary mb-2">
                  <i className="fa-solid fa-user me-2"></i>Patient Changes
                </h6>
                <table className="table table-bordered table-sm mb-4">
                  <thead className="table-light">
                    <tr>
                      <th>Field</th>
                      <th className="text-danger">Old Value</th>
                      <th className="text-success">New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changesDiff.patientDiff.map((d, i) => (
                      <tr key={i}>
                        <td className="fw-bold">{d.label}</td>
                        <td className="text-danger">{d.oldVal}</td>
                        <td className="text-success">{d.newVal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {changesDiff.accountDiff.length > 0 && (
              <>
                <h6 className="text-primary mb-2">
                  <i className="fa-solid fa-file-invoice me-2"></i>Account Changes
                </h6>
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>Field</th>
                      <th className="text-danger">Old Value</th>
                      <th className="text-success">New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changesDiff.accountDiff.map((d, i) => (
                      <tr key={i}>
                        <td className="fw-bold">{d.label}</td>
                        <td className="text-danger">{d.oldVal}</td>
                        <td className="text-success">{d.newVal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {changesDiff.patientDiff.length === 0 &&
              changesDiff.accountDiff.length === 0 &&
              moveMode === "NONE" &&
              !removeMedBulk && !(createOrphanAccount && !account) && (
              <p className="text-muted text-center py-3">No changes detected.</p>
            )}
          </>
        )}
        </Modal.Body>

        <Modal.Footer>
          {modalStep === "edit" && (
            <div className="text-center">
              <Button
                variant="secondary"
                onClick={handleSaveClick}
                className="text-white mt-1 w-100 d-flex align-items-center justify-content-center gap-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa-regular fa-floppy-disk"></i>
                    Submit
                  </>
                )}
              </Button>
            </div>
          )}
          {modalStep === "account" && (
            <div className="d-flex justify-content-between w-100">
              <Button variant="outline-secondary" onClick={() => setModalStep("edit")}>
                <i className="fa-solid fa-arrow-left me-2"></i>
                Back
              </Button>
              <Button variant="secondary" className="text-white" onClick={handleSaveClick}>
                Next
                <i className="fa-solid fa-arrow-right ms-2"></i>
              </Button>
            </div>
          )}
          {modalStep === "confirm" && (
            <div className="d-flex justify-content-between w-100">
              <Button variant="outline-secondary" onClick={() => setModalStep(createOrphanAccount && !account ? "account" : "edit")}>
                <i className="fa-solid fa-arrow-left me-2"></i>
                Back
              </Button>
              <Button variant="primary" onClick={executeSave} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-check me-2"></i>
                    Confirm & Save
                  </>
                )}
              </Button>
            </div>
          )}
        </Modal.Footer>
      </Modal>

      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
      <MedicalAidsReferenceModal
        show={showMedAidRef}
        onHide={() => setShowMedAidRef(false)}
      />
    </>
  );
}

export function useEditPatientModal(onSave) {
  const [show, setShow] = useState(false);
  const [pid, setPid] = useState(null);

  const open = (patientId) => {
    setPid(patientId || null);
    setShow(true);
  };
  const close = () => setShow(false);

  // Renderable modal element
  const Modal = () => (
    <PatientEditModal show={show} onHide={close} patientUpdateId={pid} onSave={onSave} />
  );

  return { open, Modal };
}
